import type { WaTemplate } from '../../../mocks/whatsapp';

interface Props {
  template: WaTemplate;
  onClose: () => void;
  onUse: () => void;
}

export default function TemplatePreviewModal({ template, onClose, onUse }: Props) {
  const renderBody = (text: string) => {
    return text.split('\n').map((line, i) => {
      const formatted = line.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
      return (
        <p key={i} className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted || '&nbsp;' }} />
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg mx-4 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${template.color}18` }}
            >
              <i className={`${template.icon} text-lg`} style={{ color: template.color }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">{template.name}</h2>
              <p className="text-xs text-gray-400">{template.category} Template</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Message Preview</p>

          {/* Phone mockup */}
          <div
            className="rounded-2xl p-4 min-h-48"
            style={{ backgroundColor: '#e5ddd5', backgroundImage: 'radial-gradient(circle, #d4cbc3 1px, transparent 1px)', backgroundSize: '20px 20px' }}
          >
            {/* Bubble */}
            <div className="bg-white rounded-2xl rounded-tl-sm p-4 max-w-xs shadow-sm">
              {template.headerText && (
                <p className="text-sm font-semibold text-gray-800 mb-2">{template.headerText}</p>
              )}
              <div className="space-y-0.5">
                {renderBody(template.bodyText)}
              </div>
              {template.footerText && (
                <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">{template.footerText}</p>
              )}
              <p className="text-xs text-gray-400 text-right mt-2">9:14 AM ✓✓</p>
            </div>

            {/* CTA Buttons */}
            {template.buttons && template.buttons.length > 0 && (
              <div className="mt-2 space-y-1.5 max-w-xs">
                {template.buttons.map((btn, i) => (
                  <div key={i} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white shadow-sm text-sm font-medium" style={{ color: '#25D366' }}>
                    <i className={`${btn.type === 'URL' ? 'ri-external-link-line' : btn.type === 'PHONE' ? 'ri-phone-line' : 'ri-chat-check-line'} text-base`} />
                    {btn.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variables */}
          <div className="mt-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Template Variables ({template.variables.length})</p>
            <div className="grid grid-cols-2 gap-2">
              {template.variables.map((v) => (
                <div key={v.key} className="p-2.5 bg-gray-50 rounded-lg">
                  <p className="text-xs font-mono text-gray-400">{`{{${v.key}}}`}</p>
                  <p className="text-xs font-medium text-gray-700 mt-0.5">{v.label}</p>
                  <p className="text-xs text-gray-400 truncate">e.g. {v.example}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
            <div className="text-center">
              <p className="text-base font-semibold text-gray-800">{template.usageCount}</p>
              <p className="text-xs text-gray-400">Times Sent</p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center">
              <p className="text-base font-semibold text-gray-800">{template.lastUsed}</p>
              <p className="text-xs text-gray-400">Last Used</p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${template.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                {template.status}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            Close
          </button>
          {template.status === 'Active' && (
            <button
              onClick={onUse}
              className="flex items-center gap-2 px-5 py-2 text-sm text-white rounded-lg font-medium cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#25D366' }}
            >
              <i className="ri-send-plane-fill" />
              Use This Template
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
